import React, { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '@medflex/components/stories/InputField';
import Badge, { KINDS } from '@medflex/components/stories/Badge';
import Button from '@medflex/components/stories/Button';
import { IconFilterOutline, IconSearch, IconClose } from '@medflex/components/stories/Icons';
import SelectMenu, { Item as SelectMenuItem } from '@medflex/components/stories/SelectMenu';
import { useForm } from 'react-hook-form';
import useLocationSearchParams from '@medflex/hooks/useLocationSearchParams';
import {
  FILTER_VALUES,
  TIME_RANGES,
  TIME_RANGES_FUTURE,
  TIME_RANGES_PAST,
  TIME_RANGE_VALUES,
} from './constants';
import useUserProfile from '../../hooks/useUserProfile';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import useSessionMeetingsList from './useSessionMeetingsList';
import FilterStorage from './FilterStorage';

const sortUsers = (userA, userB) => {
  const fullNameWithTitleA = userA.fullNameWithTitle.toUpperCase();
  const fullNameWithTitleB = userB.fullNameWithTitle.toUpperCase();

  if (fullNameWithTitleA < fullNameWithTitleB) return -1;
  if (fullNameWithTitleA > fullNameWithTitleB) return 1;
  return 0;
};

const useArrayChanged = (array = []) => {
  const [updatedArray, setUpdatedArray] = useState(array);

  useEffect(() => {
    if (array.length !== updatedArray.length || array.some(item => !updatedArray.includes(item))) {
      setUpdatedArray(array);
    }
  }, [array]);

  return updatedArray;
};

const Filters = () => {
  const { staff } = useAddressBookContacts();
  const { userProfile } = useUserProfile();
  const { t } = useTranslation('global', { keyPrefix: 'sessions.SessionsListModule' });
  const { filter = FILTER_VALUES.FUTURE } = useLocationSearchParams();
  const { filter: filterMeetingList } = useSessionMeetingsList();
  const defaultValues = {
    time: TIME_RANGES.NONE,
    userIds: [userProfile.id],
  };
  const {
    register: registerFormElement,
    reset,
    watch,
  } = useForm({
    defaultValues: FilterStorage[filter] || defaultValues,
  });

  const userIds = useArrayChanged(watch('userIds'));
  const timeOption = watch('time');
  const query = watch('query');

  useEffect(() => {
    reset(FilterStorage[filter]);
  }, [filter]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const isValidTimeOption = Object.values(TIME_RANGES).includes(timeOption);
      const time = isValidTimeOption ? timeOption : defaultValues.time;
      FilterStorage[filter] = { userIds, time };
      filterMeetingList({ userIds, time: TIME_RANGE_VALUES[time], query });
    }, 100);
    return () => {
      clearTimeout(timeout);
    };
  }, [userIds, timeOption, query, filter]);

  const users = useMemo(() => {
    if (userProfile && staff.length) {
      const { id, attributes } = userProfile;

      return [
        {
          id,
          fullNameWithTitle: attributes.fullNameWithTitle,
        },
        ...staff
          .map(({ user }) => ({
            id: user.id,
            fullNameWithTitle: user.fullNameWithTitle,
          }))
          .sort(sortUsers),
      ];
    }
    return [];
  }, [userProfile, staff]);

  return (
    <form
      className="flex flex-col lg:flex-row lg:space-x-2 space-y-2 lg:space-y-0 lg:items-center"
      onSubmit={e => e.preventDefault()}
    >
      <IconFilterOutline className="hidden lg:block" />
      <div className="flex-auto lg:max-w-sm lg:ml-2">
        <InputField
          className="border-slate-200 focus:border-blue-500"
          type="search"
          placeholder={t('filters.search_placeholder')}
          defaultValue=""
          afterInput={
            <div className="pr-4 flex justiy-center items-center">
              <IconSearch size="5" className="fill-current text-slate-500" />
            </div>
          }
          name="query"
          ref={registerFormElement()}
        />
      </div>
      {users && users.length > 1 && (
        <div className="lg:max-w-xs">
          <SelectMenu
            label={
              <div className="flex">
                <span>{t('filters.appointment')}</span>
                {userIds.length > 0 && (
                  <Badge className="ml-1" kind={KINDS.SECONDARY}>
                    {userIds.length}
                  </Badge>
                )}
              </div>
            }
            multiple
          >
            <div className="overflow-auto max-h-[250px]">
              {users.map(user => (
                <SelectMenuItem
                  key={user.id}
                  value={user.id}
                  label={
                    user.id === userProfile.id
                      ? t('filters.my_appointments')
                      : user.fullNameWithTitle
                  }
                  name="userIds"
                  ref={registerFormElement()}
                />
              ))}
            </div>
          </SelectMenu>
        </div>
      )}
      <div className="lg:max-w-xs">
        <SelectMenu label={t(`filters.timeLabels.${timeOption}`)}>
          <div className="overflow-auto max-h-[250px]">
            {(filter === FILTER_VALUES.PAST ? TIME_RANGES_PAST : TIME_RANGES_FUTURE).map(key => (
              <SelectMenuItem
                key={key}
                value={key}
                label={t(`filters.timeLabels.${key}`)}
                name="time"
                ref={registerFormElement()}
              />
            ))}
          </div>
        </SelectMenu>
      </div>
      <div className="hidden lg:block">
        <Button
          shape="circle"
          size="sm"
          kind="secondary:ghost"
          onClick={() => reset({ time: TIME_RANGES.NONE, userIds: [], query: '' })}
        >
          <IconClose size="text" />
        </Button>
      </div>
    </form>
  );
};

export default Filters;
